import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { Close as CloseIcon, BugReport } from '@mui/icons-material';

interface BugReportModalProps {
  open: boolean;
  onClose: () => void;
}

const BugReportModal: React.FC<BugReportModalProps> = ({ open, onClose }) => {
  // Simplified form: only email and brief description
  const [formData, setFormData] = useState({
    email: '',
    description: ''
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (field: string) => (event: any) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email je povinný';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Zadejte platný email';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Stručný popis je povinný';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const subject = encodeURIComponent('Nahlášení problému – PravniBot.cz');
      const body = encodeURIComponent(
        `Email: ${formData.email}\n\nPopis problému:\n${formData.description}`
      );

      window.location.href = `mailto:info@pravnibot.cz?subject=${subject}&body=${body}`;

      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
        setFormData({
          email: '',
          description: ''
        });
      }, 2000);
    } catch (error) {
      console.error('Chyba při otevírání emailového klienta:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 20px 60px rgba(0, 0, 0, 0.25)'
        }
      }}
    >
      <DialogTitle sx={{ p: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <BugReport sx={{ fontSize: 28, color: '#e72f0d' }} />
            <Box>
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 500,
                fontSize: '24px',
                color: '#1F1F1F'
              }}>
                Nahlásit problém
              </Typography>
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: '14px',
                color: '#666666',
                mt: 0.5
              }}>
                Pomožte nám zlepšit PravniBot.cz
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#666666' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: 2 }}>
        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Děkujeme! Váš problém byl nahlášen a brzy se na něj podíváme.
          </Alert>
        ) : (
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              fullWidth
              label="Email *"
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              placeholder="vas@email.cz"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                },
                '& .MuiInputLabel-root': {
                  fontWeight: 400,
                  '&.Mui-focused': {
                    fontWeight: 400
                  }
                }
              }}
            />

            <TextField
              fullWidth
              label="Stručný popis problému *"
              multiline
              rows={4}
              value={formData.description}
              onChange={handleChange('description')}
              error={!!errors.description}
              helperText={errors.description}
              placeholder="V několika větách popište problém"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                },
                '& .MuiInputLabel-root': {
                  fontWeight: 400,
                  '&.Mui-focused': {
                    fontWeight: 400
                  }
                }
              }}
            />

            <Button
              type="submit"
              fullWidth
              disabled={loading}
              startIcon={<BugReport />}
              sx={{
                mt: 2,
                padding: '16px 32px',
                bgcolor: '#1F1F1F !important',
                backgroundColor: '#1F1F1F !important',
                background: '#1F1F1F !important',
                color: '#FFFFFF !important',
                fontSize: '16px',
                fontWeight: 500,
                textTransform: 'none',
                border: 'none !important',
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: '#e72f0d !important',
                  backgroundColor: '#e72f0d !important',
                  background: '#e72f0d !important',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(231, 47, 13, 0.25)'
                },
                '&:disabled': {
                  bgcolor: '#CCCCCC !important',
                  color: '#666666 !important'
                }
              }}
            >
              {loading ? 'Odesílám...' : 'Odeslat nahlášení'}
            </Button>

            <Typography sx={{
              fontFamily: 'Inter',
              fontSize: '12px',
              color: '#666666',
              textAlign: 'center'
            }}>
              * Povinná pole
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BugReportModal;
